/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import clsx from 'clsx';
import React, { useMemo, useCallback } from 'react';

import { ForecastListItem, ForecastComparisonCellMeta } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { useIntl } from '@futrli/localisation';

import { FinData } from '../../../types';

import styles from '../report-table.scss';
import { TableCell } from '../utils/table-data-builder';

interface Props {
  data: (FinData | undefined)[];
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[];
  hasComparisonData?: boolean;
  firstFinField: string | undefined;
  headerStyles: Record<string, string>;
  forecastsStyles: Record<string, string>;
  columnWidth: number;
  firstColumnWidth: number;
  maxColumnsPerTable: number;
}

export const useTableHeaders = ({
  data,
  selectedForecasts,
  hasComparisonData,
  firstFinField,
  headerStyles,
  forecastsStyles,
  columnWidth,
  firstColumnWidth,
  maxColumnsPerTable
}: Props) => {
  const intl = useIntl();
  const localizedTrackerForecast = intl.formatMessage({
    id: 'reporting.daily_tracker_forecast',
    defaultMessage: 'Daily tracker forecast'
  });
  const localizedActualData = intl.formatMessage({ id: 'reporting.actual_data', defaultMessage: 'Actual data' });
  const localizedPriorYearData = intl.formatMessage({
    id: 'reporting.prior_year_actuals',
    defaultMessage: 'Prior year actuals'
  });

  const colSpan = useMemo(
    () =>
      hasComparisonData && data[0]?.columns?.length && data[0]?.periods?.length
        ? data[0]?.columns?.length / data[0]?.periods?.length
        : selectedForecasts.length,
    [data, hasComparisonData, selectedForecasts.length]
  );

  const tableHeadersArray = useMemo(() => {
    if (selectedForecasts.length <= 1) {
      return { primary: [], secondary: [] };
    }
    const columns: { primary: TableCell[]; secondary: TableCell[] } = {
      primary: [
        {
          key: 'primary-empty-column',
          value: '',
          width: firstColumnWidth,
          classes: clsx(forecastsStyles.headerEmptyCell)
        }
      ],
      secondary: [
        {
          key: 'secondary-empty-column',
          value: '',
          width: firstColumnWidth,
          classes: clsx(forecastsStyles.headerEmptyCell)
        }
      ]
    };
    if (!hasComparisonData) {
      data[0]?.periods.forEach((e, i) =>
        selectedForecasts.map(sf => {
          const fdata = data.find(d => d?.forecastId === sf.id);
          const columnMeta = firstFinField ? data[0]?.field_meta[firstFinField]?.column_meta[i] : undefined;
          columns.primary.push({
            key: `${i}-${e}-${sf.id}`,
            value: sf.name,
            width: columnWidth,
            classes: clsx(headerStyles.row, columnMeta?.cell_type === 'variance' ? forecastsStyles.rowVariance : '')
          });
          columns.secondary.push({
            key: `${i}-${e}-${sf.id}`,
            value: fdata?.periods[i] || '',
            width: columnWidth,
            classes: clsx(
              forecastsStyles.row,
              columnMeta?.cell_type === 'variance' ? forecastsStyles.rowVariance : '',
              selectedForecasts.length > 1 && styles.centerAlignedCell
            )
          });
        })
      );
    } else {
      data[0]?.columns?.forEach((e, i) => {
        const columnMeta = firstFinField
          ? ((data[0]?.field_meta[firstFinField]?.column_meta[i] as unknown) as ForecastComparisonCellMeta)
          : undefined;
        let heading = e;
        let subHeading = data[0]?.sub_headings?.[i];
        if (heading === 'Total') {
          const temp = subHeading;
          subHeading = heading;
          heading = temp || '';
        }

        if (
          subHeading &&
          columnMeta?.period &&
          (subHeading.includes(localizedPriorYearData) || subHeading.includes(localizedActualData))
        ) {
          const bits = subHeading.split(' vs '),
            cBits = columnMeta.period.split(' vs ');
          if (bits.length > 1 && cBits.length > 1) {
            subHeading = bits
              .map((b, ind) =>
                b.includes(localizedPriorYearData)
                  ? `${cBits[ind]} ${b.replace(localizedPriorYearData, '')}`.trim()
                  : b.includes(localizedActualData)
                  ? `${cBits[ind]} ${b.replace(localizedActualData, '')}`.trim()
                  : b
              )
              .join(' vs ');
          }
        }
        columns.primary.push({
          key: `${e}-${i}`,
          value: heading,
          width: columnWidth,
          classes: clsx(
            headerStyles.row,
            columnMeta?.cell_type === 'variance' ? forecastsStyles.rowVariance : '',
            columnMeta?.cell_type === 'variance' ? headerStyles.rowVariance : ''
          )
        });
        columns.secondary.push({
          key: `${e}-${i}`,
          value: subHeading,
          width: columnWidth,
          classes: clsx(forecastsStyles.row, columnMeta?.cell_type === 'variance' ? forecastsStyles.rowVariance : '')
        });
      });
    }
    return columns;
  }, [
    localizedActualData,
    localizedPriorYearData,
    hasComparisonData,
    forecastsStyles,
    headerStyles,
    data,
    selectedForecasts,
    firstFinField,
    columnWidth,
    firstColumnWidth
  ]);

  const renderTableHeaders = useCallback(
    (start: number, end: number) => {
      const minColSpan = Math.min(colSpan, maxColumnsPerTable, end - start);
      const subPeriods = data[0]?.periods?.slice(Math.floor(start / colSpan), Math.ceil(end / colSpan)) || [];
      return (
        <thead>
          <tr className={clsx(styles.tableHeader)}>
            <td
              className={clsx(selectedForecasts.length === 1 && headerStyles.headerEmptyCell)}
              style={{
                minWidth: firstColumnWidth,
                maxWidth: firstColumnWidth
              }}
            ></td>
            {tableHeadersArray.primary.length
              ? tableHeadersArray.primary.slice(start + 1, end + 1).map(ph => (
                  <th key={ph.key} className={ph.classes} style={{ minWidth: ph.width, maxWidth: ph.width }}>
                    {ph.value}
                  </th>
                ))
              : subPeriods.map((e, i) => {
                  const columnMeta = firstFinField ? data[0]?.field_meta[firstFinField]?.column_meta[i] : undefined;
                  let realColSpan = minColSpan;
                  if (i === 0) {
                    realColSpan = Math.min(colSpan - (start % colSpan), minColSpan);
                  } else if (i === subPeriods.length - 1) {
                    realColSpan = end % colSpan || minColSpan;
                  }
                  return (
                    <th
                      key={`${i}-${e}`}
                      className={clsx(
                        headerStyles.row,
                        columnMeta?.cell_type === 'variance' && !hasComparisonData ? headerStyles.rowVariance : '',
                        selectedForecasts.length < 2 && firstFinField && columnMeta?.is_first_in_group
                          ? styles.autoLeftBorder
                          : '',
                        selectedForecasts.length < 2 && firstFinField && columnMeta?.is_first_in_group
                          ? styles.autoRightBorder
                          : '',
                        selectedForecasts.length > 1 && styles.centerAlignedCell
                      )}
                      style={{
                        minWidth: columnWidth * realColSpan,
                        maxWidth: columnWidth * realColSpan
                      }}
                      colSpan={realColSpan}
                    >
                      {e}
                    </th>
                  );
                })}
          </tr>
          {tableHeadersArray.secondary.length > 0 && (
            <tr className={clsx(styles.tableHeader)}>
              <td
                className={tableHeadersArray.secondary[0].classes}
                style={{
                  minWidth: tableHeadersArray.secondary[0].width,
                  maxWidth: tableHeadersArray.secondary[0].width
                }}
              >
                {tableHeadersArray.secondary[0].value}
              </td>
              {tableHeadersArray.secondary.slice(start + 1, end + 1).map(sh => (
                <th key={sh.key} className={sh.classes} style={{ minWidth: sh.width, maxWidth: sh.width }}>
                  {sh.value}
                </th>
              ))}
            </tr>
          )}
        </thead>
      );
    },
    [
      data,
      firstFinField,
      hasComparisonData,
      selectedForecasts,
      headerStyles,
      tableHeadersArray,
      colSpan,
      maxColumnsPerTable,
      columnWidth,
      firstColumnWidth
    ]
  );

  return { renderTableHeaders, localizedTrackerForecast };
};
